import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

import { contactsKeys } from "./use-contacts";
import { fieldsKeys } from "./use-fields";
import { oauthErrorMessage } from "./use-oauth";
import { zoominfoKeys } from "./use-zoominfo";

/** Company news a search can keep companies for: ZoomInfo's Scoops of that kind. */
export type LeadEventKind = "expanding" | "hiring" | "acquisition" | "launch" | "partnership" | "project" | "painPoint";

/** A ZoomInfo contact search as the lead finder works it out; the customer can edit it. */
export interface LeadSearch {
	count: number;
	purpose: string;
	groupName: string;
	jobTitles: string[];
	excludeJobTitles: string[];
	managementLevels: string[];
	departments: string[];
	industries: string[];
	companyKeywords: string[];
	employeesMin: number | null;
	employeesMax: number | null;
	revenueMinMillions?: number | null;
	revenueMaxMillions?: number | null;
	companyTypes?: ("private" | "public" | "npo" | "education" | "government")[];
	/** Least yearly headcount growth, in percent: ZoomInfo's sign of a company growing. */
	headcountGrowthMin?: number | null;
	fundedWithinMonths?: number | null;
	newInRoleWithinDays?: number | null;
	/** Only companies with recent news of these kinds. */
	events?: LeadEventKind[];
	/** How recent that news must be; six months when not set. */
	eventsWithinMonths?: number | null;
	countries: string[];
	states: string[];
	metroAreas: string[];
	postalCodes: string[];
	technologies: { vendor: string; product: string }[];
	assumptions: string[];
}

export interface LeadPreview {
	id: string;
	name: string;
	title: string;
	company: string;
	phone: "both" | "direct" | "mobile" | "none";
	/** False when every number ZoomInfo holds is on its Do Not Call list. */
	callable: boolean;
	accuracy: number | null;
	/** Already one of the account's contacts: a purchase leaves them out. */
	inAccount: boolean;
	/** Their company's news, when the search asked for some: a reason to call now. */
	event?: { kind: string; date: string; text: string };
}

export interface LeadSearchResult {
	search: LeadSearch;
	total: number;
	preview: LeadPreview[];
	/** What the lead finder changed and why, oldest first. */
	notes: string[];
	/** Technologies ZoomInfo does not list, left out of the search. */
	unknown: string[];
	/** ZoomInfo's reason when it refused the search. */
	rejected: string | null;
	/** Companies with the news the search asked for, the only ones it searched, when it asked for some. */
	newsCompanies?: number;
}

/** A line of the conversation with the lead finder. */
export interface LeadMessage {
	role: "customer" | "finder";
	text: string;
	/** Answers the customer can tap, under a question. */
	options?: string[];
	/** Contacts the search matched, on a finder line that came with a search. */
	total?: number;
	/** Next asks the customer can tap to tune the search, on a finder line that came with one. */
	suggestions?: string[];
}

export interface LeadSearchPurchase {
	id: string;
	count: number;
	groupName: string;
	createdAt: string;
}

/** A lead search: the conversation that found it, and its search with a fresh free preview. */
export interface LeadConversation {
	id: string;
	title: string;
	messages: LeadMessage[];
	result: LeadSearchResult | null;
	purchases: LeadSearchPurchase[];
	/** Sent back with every change; one made from an older version is refused with a 409. */
	version: number;
	/** Why a reopened search shows no result, e.g. ZoomInfo is not answering. */
	notice: string | null;
}

export interface LeadPurchaseResult {
	/** Null when no lead could be added, so no group was made. */
	groupId: string | null;
	groupName: string;
	wanted: number;
	lookedUp: number;
	added: number;
	alreadyHad: number;
	/** People the account already had, left out before any credit was spent. */
	inAccount: number;
	failed: number;
	doNotCall: number;
	/** Flagged numbers that could not be added to the suppression list. */
	notSuppressed: number;
	skipped: number;
	stoppedEarly: "credit_limit" | "error" | "pages" | null;
}

export interface LeadPurchase {
	state: "waiting" | "active" | "completed" | "failed";
	progress: { step: "finding" | "looking-up" | "saving"; done: number; total: number } | null;
	result: LeadPurchaseResult | null;
	error: string | null;
}

/** A search in the recent list. */
export interface LeadSearchSummary {
	id: string;
	title: string;
	total: number | null;
	updatedAt: string;
}

export const leadFinderKeys = {
	searches: (pluginId: string) => [...zoominfoKeys.all, "lead-searches", pluginId] as const,
	search: (pluginId: string, id: string | null) => [...zoominfoKeys.all, "lead-search", pluginId, id] as const,
	purchase: (pluginId: string, id: string | null) => [...zoominfoKeys.all, "lead-purchase", pluginId, id] as const,
	fields: (pluginId: string) => [...zoominfoKeys.all, "lead-fields", pluginId] as const,
};

const base = (pluginId: string) => `/plugins/${pluginId}/zoominfo/leads`;
const onError = (error: any) => {
	logger.error(error);
	toast.error(oauthErrorMessage(error));
};

/** The account's most recent searches, newest first. */
export function useLeadSearches(pluginId: string) {
	return useQuery({
		queryKey: leadFinderKeys.searches(pluginId),
		queryFn: async (): Promise<LeadSearchSummary[]> => {
			const response = await apiClient.get(`${base(pluginId)}/searches`);
			return response.data;
		},
		enabled: Boolean(pluginId),
	});
}

/** A kept search; reopening one runs its search again for a fresh preview. */
export function useLeadSearch(pluginId: string, id: string | null) {
	return useQuery({
		queryKey: leadFinderKeys.search(pluginId, id),
		queryFn: async (): Promise<LeadConversation> => {
			const response = await apiClient.get(`${base(pluginId)}/searches/${id}`);
			return response.data;
		},
		enabled: Boolean(pluginId && id),
		staleTime: Infinity,
	});
}

/** A 409 means the search changed elsewhere: the page reloads it, so the customer sees what would be changed or bought. */
function useStaleSearchError(pluginId: string) {
	const queryClient = useQueryClient();
	return (error: any, id?: string) => {
		onError(error);
		if (id && error?.response?.status === 409)
			void queryClient.invalidateQueries({ queryKey: leadFinderKeys.search(pluginId, id) });
	};
}

/** Every change answers with the whole conversation, which then serves the search's own query. */
function useConversationMutation<T>(pluginId: string, request: (input: T) => Promise<{ data: LeadConversation }>) {
	const queryClient = useQueryClient();
	const staleSearchError = useStaleSearchError(pluginId);
	return useMutation({
		mutationFn: async (input: T): Promise<LeadConversation> => {
			const response = await request(input);
			return response.data;
		},
		onSuccess: (conversation) => {
			queryClient.setQueryData(leadFinderKeys.search(pluginId, conversation.id), conversation);
			// The search moves to the top of the recent list, under its latest title.
			void queryClient.invalidateQueries({ queryKey: leadFinderKeys.searches(pluginId) });
		},
		// Only a change to a kept search names one; a first line has nothing to reload.
		onError: (error, input) => staleSearchError(error, (input as { id?: string }).id),
	});
}

/** A new search from the customer's first line. */
export function useStartLeadSearch(pluginId: string) {
	return useConversationMutation(pluginId, (text: string) => apiClient.post(`${base(pluginId)}/searches`, { text }));
}

export function useReplyToLeadSearch(pluginId: string) {
	return useConversationMutation(pluginId, ({ id, version, text }: { id: string; version: number; text: string }) =>
		apiClient.post(`${base(pluginId)}/searches/${id}/messages`, { version, text })
	);
}

/** The filters as the customer edited them, searched again for free. */
export function useEditLeadSearch(pluginId: string) {
	return useConversationMutation(
		pluginId,
		({ id, version, search }: { id: string; version: number; search: LeadSearch }) =>
			apiClient.put(`${base(pluginId)}/searches/${id}`, { version, search })
	);
}

export function useBuyLeads(pluginId: string) {
	const queryClient = useQueryClient();
	const staleSearchError = useStaleSearchError(pluginId);
	return useMutation({
		mutationFn: async (input: {
			searchId: string;
			version: number;
			count: number;
			groupName: string;
		}): Promise<{ id: string }> => {
			const { searchId, ...body } = input;
			const response = await apiClient.post(`${base(pluginId)}/searches/${searchId}/purchases`, body);
			return response.data;
		},
		// The search lists its purchases, as the API now does too, and moves to the top of the recent list.
		onSuccess: ({ id }, { searchId, count, groupName }) => {
			queryClient.setQueryData<LeadConversation>(
				leadFinderKeys.search(pluginId, searchId),
				(conversation) =>
					conversation && {
						...conversation,
						purchases: [...conversation.purchases, { id, count, groupName, createdAt: new Date().toISOString() }],
					}
			);
			void queryClient.invalidateQueries({ queryKey: leadFinderKeys.searches(pluginId) });
		},
		onError: (error, input) => staleSearchError(error, input.searchId),
	});
}

/** Checks this many times in a row without an answer before the page stops waiting on a purchase. */
const PURCHASE_CHECKS = 3;

/** Polls a purchase every two seconds until it ends, or until it cannot be checked PURCHASE_CHECKS times in a row. */
export function useLeadPurchase(pluginId: string, id: string | null) {
	return useQuery({
		queryKey: leadFinderKeys.purchase(pluginId, id),
		queryFn: async (): Promise<LeadPurchase> => {
			const response = await apiClient.get(`${base(pluginId)}/purchases/${id}`);
			return response.data;
		},
		enabled: Boolean(pluginId && id),
		// A failed check is retried in place, so an error means PURCHASE_CHECKS in a row; a success in between resets it.
		retry: PURCHASE_CHECKS - 1,
		retryDelay: 2000,
		refetchInterval: (query) =>
			["completed", "failed"].includes(query.state.data?.state ?? "") || query.state.status === "error" ? false : 2000,
	});
}

/** A contact field bought leads fill. */
export interface LeadField {
	name: string;
	label: string;
}

/** Fields leads fill that the account has not set up; the contacts list hides values without one. */
export function useMissingLeadFields(pluginId: string) {
	return useQuery({
		queryKey: leadFinderKeys.fields(pluginId),
		queryFn: async (): Promise<LeadField[]> => {
			const response = await apiClient.get(`${base(pluginId)}/fields`);
			return response.data;
		},
		enabled: Boolean(pluginId),
	});
}

export function useCreateLeadFields(pluginId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (): Promise<LeadField[]> => {
			const response = await apiClient.post(`${base(pluginId)}/fields`);
			return response.data;
		},
		onSuccess: (created) => {
			queryClient.setQueryData(leadFinderKeys.fields(pluginId), []);
			void queryClient.invalidateQueries({ queryKey: fieldsKeys.lists() });
			void queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
			toast.success(`Added ${created.length} contact fields.`);
		},
		onError,
	});
}
